import { customerInfoConstants } from '../../constants';
import { IFormInitialState, lookupAddressFieldError } from '../../errorLogic';
import { useMemo, useEffect, useState } from 'react';
import styles from '../../billing-address.module.scss';
import Input from '../input';
import { AddressAutoSuggest } from '../address-auto-suggest/address-auto-suggest';
import { IPrevRefValues } from '../on-blur-handler';
import { IStateProvinces } from '~/interfaces/states-provinces';
import { AutoSuggestionOptions } from '~/gql/__generated__/types';
import { type Address } from '~/interfaces/azure-maps';
import { debounce } from 'lodash';
import { fetchAddressFromAzureMaps } from '~/utils/azure-maps-client';

interface IAddressInputProps {
  formState: IFormInitialState;
  addressFocus: React.RefObject<boolean>;
  onBlurHandler: (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
    prevRefValues: React.RefObject<IPrevRefValues>,
    formState: IFormInitialState,
  ) => void;
  updateFormState: (name: string, value: string, errorMsg?: string) => void;
  handleFocus: () => void;
  prevRefValues: React.RefObject<IPrevRefValues>;
  stateProvinces: IStateProvinces[] | undefined;
  autoSuggestionOptions: AutoSuggestionOptions | null;
  maxCharLength: number;
}
export const AddressInput: React.FC<IAddressInputProps> = ({
  formState,
  addressFocus,
  onBlurHandler,
  updateFormState,
  handleFocus,
  prevRefValues,
  stateProvinces,
  autoSuggestionOptions,
  maxCharLength,
}) => {
  const { address } = customerInfoConstants.inputs;
  const [suggestions, setSuggestions] = useState<Address[]>([]);
  const [openSuggestionPanel, setOpenSuggestionPanel] = useState(false);
  const maxResults = autoSuggestionOptions?.maxResults || 5;

  const debouncedFetch = useMemo(
    () =>
      debounce(async (inputValue: string) => {
        const response = await fetchAddressFromAzureMaps(
          inputValue.trim(),
          true,
          maxResults,
        );
        const suggestedAddresses = response?.results
          ?.map((x) => x.address)
          .filter(
            (y) =>
              y.postalCode &&
              !['po box', 'post office box'].some((box) =>
                y.freeformAddress?.toLowerCase().includes(box),
              ),
          );
        console.log('Suggested Addresses from Azure Maps:', suggestedAddresses);
        setSuggestions(suggestedAddresses);
        if (suggestedAddresses.length > 0 && addressFocus.current) {
          setOpenSuggestionPanel(true);
        } else {
          setOpenSuggestionPanel(false);
        }
      }, 300),
    [addressFocus, maxResults],
  );

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    const errorMsg = lookupAddressFieldError(address, inputValue);
    updateFormState(inputName, inputValue, errorMsg ?? '');
    if (!inputValue || inputValue.length < 3) {
      setSuggestions([]);
      setOpenSuggestionPanel(false);
      debouncedFetch.cancel();
      return;
    }
    void debouncedFetch(inputValue);
  };

  const OnBlurCalls = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlurHandler(e, prevRefValues, formState);
    const inputName = e.target.name;
    const inputValue = e.target.value;
    const errorMsg = lookupAddressFieldError(address, inputValue);
    updateFormState(inputName, inputValue, errorMsg ?? '');
    if (!inputValue) {
      setSuggestions([]);
      return;
    }
    addressFocus.current = false;
  };

  return (
    <>
      <Input
        type={address.type}
        id={address.id}
        name={address.name}
        label={address.label}
        value={formState.address.value}
        onChange={handleAddressChange}
        onFocus={handleFocus}
        placeholder={address.placeHolder}
        error={formState.address.error}
        ModuleClassName={styles.item}
        onBlur={OnBlurCalls}
        maxCharLength={maxCharLength}
      />
      <AddressAutoSuggest
        address={address}
        openSuggestionPanel={openSuggestionPanel}
        setOpenSuggestionPanel={setOpenSuggestionPanel}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        updateFormState={updateFormState}
        stateProvinces={stateProvinces}
      />
    </>
  );
};
